import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  getUserDisplayName,
} from "@bw/shared/utils";
import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import {
  getFinanceSummary,
  getPlatformFinanceSummary,
  getPlatformPayouts,
  getPlatformSettlements,
  type SettlementPeriodResponse,
} from "../api";
import { PayoutLedger } from "../components/PayoutLedger";

type SettlementState = "error" | "loading" | "mixed" | "empty" | "ok";

function MetricCard({
  label,
  value,
  supportingText,
  emphasis = false,
}: {
  label: string;
  value: string;
  supportingText?: string;
  emphasis?: boolean;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderColor: emphasis ? "primary.main" : "divider",
      }}
    >
      <CardContent>
        <Typography color="text.secondary" variant="overline">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
        {supportingText && (
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ mt: 0.75 }}
          >
            {supportingText}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function FinancePage() {
  const feedQuery = useQuery({
    queryKey: ["platform", "finance-summary"],
    queryFn: getFinanceSummary,
  });

  const settlementQuery = useQuery({
    queryKey: ["platform", "finance-settlements"],
    queryFn: () => getPlatformSettlements(),
  });

  const kpiQuery = useQuery({
    queryKey: ["platform", "finance-kpis"],
    queryFn: () => getPlatformFinanceSummary(),
  });

  const payoutQuery = useQuery({
    queryKey: ["platform", "finance-payouts"],
    queryFn: () => getPlatformPayouts(),
  });

  const entries = useMemo(
    () => feedQuery.data?.entries ?? [],
    [feedQuery.data],
  );
  const actorCount = useMemo(
    () => new Set(entries.map((entry) => entry.actor_id)).size,
    [entries],
  );

  const settlement = settlementQuery.data;
  const periods = settlement?.periods ?? [];
  const hasSettlements = periods.length > 0;
  const aggregateCurrency = settlement?.currency ?? null;
  const isMixedCurrency = hasSettlements && aggregateCurrency === null;

  const summaryState: SettlementState = settlementQuery.isError
    ? "error"
    : settlementQuery.isLoading
      ? "loading"
      : !hasSettlements
        ? "empty"
        : isMixedCurrency
          ? "mixed"
          : "ok";

  const revenueValue =
    summaryState === "error"
      ? "Unavailable"
      : summaryState === "ok" && settlement
        ? formatMoney(
            settlement.total_platform_fees,
            aggregateCurrency ?? "USD",
          )
        : "—";
  const netOwedValue =
    summaryState === "error"
      ? "Unavailable"
      : summaryState === "ok" && settlement
        ? formatMoney(settlement.total_net_owed, aggregateCurrency ?? "USD")
        : "—";

  const kpi = kpiQuery.data;
  const kpiState: "error" | "loading" | "ok" = kpiQuery.isError
    ? "error"
    : kpiQuery.isLoading
      ? "loading"
      : "ok";

  const kpiMoney = (value: string | null | undefined): string => {
    if (kpiState === "error") return "Unavailable";
    if (kpiState === "loading") return "—";
    return value == null ? "—" : formatMoney(value, kpi?.currency ?? "USD");
  };

  const pendingPayoutCopy =
    kpiState === "ok" && kpi
      ? `${kpi.pending_payout_count.toLocaleString("en-US")} ${
          kpi.pending_payout_count === 1 ? "payout" : "payouts"
        } in transit`
      : undefined;

  return (
    <PlatformPage
      title="Finance"
      description="Platform revenue and transaction activity."
    >
      <Stack spacing={3}>
        <Box component="section" aria-label="Finance summary">
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <MetricCard
                label="Pending payouts"
                value={kpiMoney(kpi?.pending_payout_amount)}
                supportingText={pendingPayoutCopy}
              />
              <MetricCard
                label="Commission (MTD)"
                value={kpiMoney(kpi?.commission_mtd)}
                emphasis
              />
              <MetricCard
                label="Revenue (MTD)"
                value={kpiMoney(kpi?.revenue_mtd)}
              />
            </Box>

            {summaryState === "error" && (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => settlementQuery.refetch()}
                  >
                    Try again
                  </Button>
                }
              >
                Couldn’t load settlements. Figures are unavailable.
              </Alert>
            )}

            {summaryState === "mixed" && (
              <Typography color="text.secondary" variant="body2">
                Multiple currencies this period — totals aren’t aggregable; see
                the per-period rows below.
              </Typography>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <MetricCard label="Total revenue" value={revenueValue} emphasis />
              <MetricCard label="Net owed to facilities" value={netOwedValue} />
              <NextPayoutCard
                state={summaryState}
                nextPayoutDate={
                  hasSettlements ? settlement?.next_payout_date : null
                }
              />
            </Box>
          </Stack>
        </Box>

        {!settlementQuery.isError && (
          <SettlementDetail
            isLoading={settlementQuery.isLoading}
            periods={periods}
            aggregateCurrency={aggregateCurrency}
          />
        )}

        {payoutQuery.isError ? (
          <Card component="section" variant="outlined">
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                px: 2.5,
                py: 2,
              }}
            >
              <Typography
                component="h2"
                variant="subtitle1"
                sx={{ fontWeight: 700 }}
              >
                Payout history
              </Typography>
            </Box>
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void payoutQuery.refetch()}
                >
                  Try again
                </Button>
              }
              sx={{ m: 2 }}
            >
              Couldn’t load payout history. Payout records are unavailable.
            </Alert>
          </Card>
        ) : (
          <PayoutLedger
            isLoading={payoutQuery.isLoading}
            payouts={payoutQuery.data?.payouts ?? []}
            currency={payoutQuery.data?.currency ?? null}
            total={payoutQuery.data?.total ?? 0}
          />
        )}

        <Card
          component="section"
          role="region"
          aria-label="Recent transaction events"
          variant="outlined"
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: 1,
              borderColor: "divider",
              px: 2.5,
              py: 2,
            }}
          >
            <Typography
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Recent transaction events
            </Typography>
            {!feedQuery.isLoading && !feedQuery.isError && (
              <Typography color="text.secondary" variant="caption">
                {actorCount.toLocaleString("en-US")}{" "}
                {actorCount === 1 ? "actor" : "actors"}
              </Typography>
            )}
          </Stack>

          {feedQuery.isLoading ? (
            <Box
              role="status"
              aria-label="Loading transaction events"
              sx={{ display: "flex", justifyContent: "center", py: 5 }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : feedQuery.isError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => feedQuery.refetch()}
                >
                  Try again
                </Button>
              }
              sx={{ m: 2 }}
            >
              Couldn’t load activity. The transaction feed failed to load.
            </Alert>
          ) : entries.length === 0 ? (
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ px: 2.5, py: 4 }}
            >
              No transaction events yet.
            </Typography>
          ) : (
            <List disablePadding>
              {entries.map((entry) => (
                <ListItem
                  key={entry.entry_id}
                  divider
                  sx={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
                      {entry.action}
                    </Typography>
                    <Typography
                      noWrap
                      color="text.secondary"
                      variant="caption"
                      sx={{ display: "block" }}
                    >
                      {entry.resource_type} · {entry.resource_id}
                    </Typography>
                    <Typography
                      noWrap
                      color="text.secondary"
                      variant="caption"
                      sx={{ display: "block" }}
                    >
                      {getUserDisplayName(
                        entry.actor_display_name,
                        entry.actor_id,
                      )}
                    </Typography>
                  </Box>
                  <Stack
                    spacing={0.75}
                    sx={{ flexShrink: 0, alignItems: "flex-end" }}
                  >
                    <StatusBadge status={entry.actor_role} />
                    <Typography color="text.secondary" variant="caption">
                      {formatDateTime(entry.created_at)}
                    </Typography>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
        </Card>
      </Stack>
    </PlatformPage>
  );
}

function NextPayoutCard({
  state,
  nextPayoutDate,
}: {
  state: SettlementState;
  nextPayoutDate?: string | null;
}) {
  const value =
    state === "error"
      ? "Unavailable"
      : nextPayoutDate
        ? formatDate(nextPayoutDate)
        : "—";
  const hasLoadedWithoutDate =
    state !== "error" && state !== "loading" && !nextPayoutDate;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography color="text.secondary" variant="overline">
          Next payout
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
        {hasLoadedWithoutDate && (
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ mt: 0.75 }}
          >
            None scheduled
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function SettlementDetail({
  isLoading,
  periods,
  aggregateCurrency,
}: {
  isLoading: boolean;
  periods: SettlementPeriodResponse[];
  aggregateCurrency: string | null;
}) {
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Box
          role="status"
          aria-label="Loading settlement periods"
          sx={{ display: "flex", justifyContent: "center", py: 6 }}
        >
          <CircularProgress size={32} />
        </Box>
      </Card>
    );
  }

  if (periods.length === 0) {
    return (
      <Card variant="outlined">
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ px: 2.5, py: 4 }}
        >
          No settlements yet.
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      component="section"
      role="region"
      aria-label="Settlement periods"
      variant="outlined"
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2.5, py: 2 }}>
        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Settlement periods
        </Typography>
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: (theme) => theme.spacing(105) }}>
          <TableHead>
            <TableRow>
              <TableCell scope="col">Period</TableCell>
              <TableCell scope="col" align="right">
                Gross
              </TableCell>
              <TableCell scope="col" align="right">
                Platform fees
              </TableCell>
              <TableCell scope="col" align="right">
                Refunds
              </TableCell>
              <TableCell scope="col" align="right">
                Net owed
              </TableCell>
              <TableCell scope="col">Status</TableCell>
              <TableCell scope="col">Payout</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.map((period) => {
              const periodCurrency =
                period.currency ?? aggregateCurrency ?? "USD";

              return (
                <TableRow
                  key={`${period.period_start}-${period.period_end}`}
                  hover
                >
                  <TableCell>
                    {formatDate(period.period_start)} –{" "}
                    {formatDate(period.period_end)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.gross, periodCurrency)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.platform_fees, periodCurrency)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.refunds, periodCurrency)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.net_owed, periodCurrency)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={period.status} />
                  </TableCell>
                  <TableCell>{formatDate(period.payout_date)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
